import axios, { CancelTokenSource } from 'axios';
import { DossierId } from '@/dossier/models/AbstractDossier';
import SyncResponse from '../models/SyncResponse';
import DriveBooking from '../models/DriveBooking';
import { parseDriveFleetSources } from '../converters/driveBookingFleetSourcesConverters';
import DriveBookingCreationData from '../models/DriveBookingCreationData';
import DriveFleetSource from '../models/DriveFleetSource';
import { parseDriveBooking, parseDriveBookings, parseDriveReservationAssets, parseSyncResponse, parseDriveBookingDetails } from '../converters/driveBookingConverters';
import DriveBookingDetails from '../models/DriveBookingDetails';
import DriveBookingUpdate from '../models/DriveBookingUpdate';

export default class DriveBookingClient {
  static async loadForDossier(dossierId: DossierId): Promise<Array<DriveBooking>> {
    const response = await axios.get(`/api/mobopro/v1/drivebookings?dossierId=${dossierId}`);
    return parseDriveBookings(response.data);
  }

  static async getById(driveBookingId: number): Promise<DriveBooking | null> {
    const response = await axios.get(`/api/mobopro/v1/drivebookings/${driveBookingId}`, { skipLoader: true });
    return parseDriveBooking(response.data);
  }

  static async syncDriveBooking(driveBookingId: number, cancelToken?: CancelTokenSource): Promise<SyncResponse> {
    const response = await axios.post(
      `/api/mobopro/v1/drivebookings/${driveBookingId}/sync`,
      null,
      {
        skipLoader: true,
        cancelToken: cancelToken?.token,
      },
    );

    return parseSyncResponse(response.data);
  }

  static async loadDetails(driveBookingId: number): Promise<DriveBookingDetails|null> {
    const response = await axios.get(`/api/mobopro/v1/drivebookings/${driveBookingId}/details`);
    return parseDriveBookingDetails(response.data);
  }

  static async updateStatus(
    driveBookingId: number,
    status: number,
    mileage: number | null,
    fuelLevel: number | null,
    note: string | null,
  ): Promise<void> {
    await axios.put(`/api/mobopro/v1/drivebookings/${driveBookingId}/status`, {
      status,
      mileage,
      fuelLevel,
      note,
    });
  }

  static async getAvailableAssets(startDate: string, endDate: string, fleetSourceId: number) {
    const response = await axios.get(`/api/mobopro/v1/drivebookings/availableAssets?startDate=${startDate}&endDate=${endDate}&fleetSourceId=${fleetSourceId}`);
    return parseDriveReservationAssets(response.data);
  }

  static async getFleetSources(outletId: number): Promise<Array<DriveFleetSource>> {
    const response = await axios.get(`/api/mobopro/v1/mobooutlets/${outletId}/drivebookings/fleetsources`);
    return parseDriveFleetSources(response.data);
  }

  static async postDriveBooking(data: DriveBookingCreationData): Promise<any> {
    const response = await axios.post('/api/mobopro/v1/drivebookings', data);
    return response.data;
  }

  static async update(driveBookingId: number, data: DriveBookingUpdate): Promise<void> {
    await axios.patch(`/api/mobopro/v1/drivebookings/${driveBookingId}`, data);
  }

  static async putDriveBookingStatus(driveBookingId: number, status: number): Promise<any> {
    const response = await axios.put(`/api/mobopro/v1/drivebookings/${driveBookingId}/status`, { status });
    return response.status;
  }
}
